import classNames from 'classnames'
import { Component } from 'react'
import { toast } from 'react-toastify'
import {
    C6_COMPONENT_MODELS,
    createSoftwareVersion,
    deleteSoftwareVersion,
    getSoftwareVersion,
    ISoftwareVersion,
    SoftwareVersionComponent,
    updateSoftwareVersion,
} from '../../api/0x0001/software-versions'
import { EspBinInput } from '../../components/esp-bin-input'
import { ChipId, IEspApp } from '../../components/esp-bin-input/esp-bin-parser'
import { MyFile } from '../../components/files'
import Loader from '../../components/loader'
import { getString } from '../../locale'
import history from '../../routes/history'
import { ClientModel } from '../../api/clients'

type SoftwareVersion = Required<Pick<ISoftwareVersion, 'version' | 'model' | 'base64Bin' | 'changeLog' | 'component'>>

type Props = {
    softwareVersionId?: ISoftwareVersion['id']
    initialModel?: ClientModel
    initialComponent?: SoftwareVersionComponent
}

type State = {
    working: boolean
    softwareVersion?: Record<keyof SoftwareVersion, string | number>
    updates: Partial<SoftwareVersion>
    errors: Partial<Record<keyof SoftwareVersion, string>>
    unsaved: boolean
    binFile?: MyFile
}

export class SoftwareVersionForm extends Component<Props, State> {
    state: State = {
        working: false,
        softwareVersion: undefined,
        updates: {},
        errors: {},
        unsaved: false,
        binFile: undefined,
    }

    componentDidMount = () => {
        this.loadSoftwareVersion()
    }

    loadSoftwareVersion = async () => {
        if (this.props.softwareVersionId) {
            try {
                const softwareVersion = (await getSoftwareVersion(this.props.softwareVersionId)).data as SoftwareVersion
                console.log('softwareVersion', softwareVersion)
                this.setState({ softwareVersion })

                if (softwareVersion.base64Bin) {
                    const buffer = Buffer.from(softwareVersion.base64Bin, 'base64')
                    const blob = new Blob([buffer], { type: 'application/octet-binary' })
                    this.setState({ binFile: blob as MyFile })
                }
            } catch (err) {
                console.error(err)
                toast.error((err as Error).message)
            }
        } else {
            this.setState({
                softwareVersion: {
                    version: '',
                    model: this.props.initialModel ?? ClientModel.Standard,
                    changeLog: '',
                    base64Bin: '',
                    component: this.props.initialComponent ?? 'app',
                },
            })
        }
    }

    handleModelChange(model: ClientModel) {
        const isC6Capable = (C6_COMPONENT_MODELS as ClientModel[]).includes(model)
        if (!isC6Capable) {
            // Only the LTSP101/70/175 family carries the ESP32-C6 coprocessor.
            const { softwareVersion, updates } = this.state
            softwareVersion!.component = 'app'
            updates.component = 'app'
            this.setState({ softwareVersion, updates, unsaved: true })
        }
        this.handleChange('model', model)
    }

    handleChange(name: keyof SoftwareVersion, value: string | number) {
        const { softwareVersion, updates, errors } = this.state
        softwareVersion![name] = value
        updates[name] = value as any
        // delete errors[name]
        this.setState({ softwareVersion, updates, unsaved: true })
    }

    private handleEspBinChange(binFile: MyFile, base64Bin: string, espApp: IEspApp) {
        this.setState({ binFile })
        // Embedded esp_app_desc versions are not guaranteed to carry the 'v' prefix
        // (e.g. the C6 network_adapter image embeds "2.7.1"). The server & devices
        // require strict v-prefixed SemVer, so normalize before filling the form.
        this.handleChange('version', this.normalizeSoftwareVersion(espApp.version))
        this.handleChange('base64Bin', base64Bin)
        // The network_adapter image (ESP32-C6 WiFi/BT coprocessor) is the 'c6' component.
        const model = Number(this.state.softwareVersion!.model)
        const isC6Capable = (C6_COMPONENT_MODELS as ClientModel[]).includes(model)
        if (isC6Capable && espApp.projectName === 'network_adapter') {
            this.handleChange('component', 'c6')
        }
    }

    private normalizeSoftwareVersion(version: string): string {
        const trimmed = version.trim()
        if (/^v/i.test(trimmed)) return trimmed
        // Re-emit v-prefixed SemVer (server requires a lowercase 'v'): 2.7.1 → v2.7.1.
        const semantic =
            /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*)?(?:\+[0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*)?$/.exec(trimmed)
        if (semantic) return `v${trimmed}`
        return trimmed
    }

    async handleSave() {
        if (this.state.working) return
        this.setState({ working: true })
        try {
            const { updates, errors } = this.state
            if (Object.keys(updates).length === 0) {
                return toast.error(getString('settings.no_updates'))
            }
            if (Object.keys(errors).length > 0) {
                return toast.error(getString('settings.fix_errors'))
            }
            if (this.props.softwareVersionId !== undefined) {
                const result = await updateSoftwareVersion(this.props.softwareVersionId, this.state.updates as any)
                console.log('updateSoftwareVersion', result)
                toast.success(getString('common.success', 'common.saved'))
            } else {
                const result = await createSoftwareVersion(this.state.softwareVersion as any)
                console.log('createSoftwareVersion', result)
                toast.success(getString('common.success', 'common.saved'))
                history.push(`/software-manager/${result.data.id}`)
                return
            }
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
        this.setState({ working: false })
    }

    async handleDelete() {
        if (this.state.working) return
        this.setState({ working: true })
        try {
            const result = await deleteSoftwareVersion(this.props.softwareVersionId!)
            console.log('deleteSoftwareVersion', result)
            toast.success(getString('common.success', 'common.delete'))
            history.push('/software-manager')
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
        this.setState({ working: false })
    }

    render() {
        if (this.state.softwareVersion === undefined) return <Loader size={14} />
        return (
            <div>
                {this.renderActions()}
                {this.renderSoftwareVersion()}
            </div>
        )
    }

    renderActions() {
        const { working } = this.state
        return (
            <div className="flex items-center">
                {this.renderActionButton(getString('common.save'), this.handleSave.bind(this), working)}
                {this.renderActionButton(
                    getString('common.delete'),
                    this.handleDelete.bind(this),
                    this.props.softwareVersionId === undefined || working,
                )}
                {this.renderUnsavedChanges()}
            </div>
        )
    }

    renderActionButton(label: string, onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void, disabled?: boolean) {
        return (
            <button
                onClick={onClick}
                className={classNames(
                    'me-4 group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-501',
                    disabled ? 'opacity-70 cursor-not-allowed' : 'duration-200 hover:bg-indigo-700',
                )}
                disabled={disabled}
            >
                {label}
            </button>
        )
    }

    renderUnsavedChanges() {
        if (!this.state.unsaved) return undefined
        return <div className="dark:text-white flex items-center">* {getString('common.unsaved_changes')}</div>
    }

    renderInputErrorMessage(message: string) {
        return <div className="text-red-500 text-sm">{message}</div>
    }

    renderSoftwareVersion() {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderModelSelect()}
                {this.renderComponentSelect()}
                {this.renderVersionInput()}
                {this.renderChangeLogInput()}
                {this.renderEspBinInput()}
            </div>
        )
    }

    renderModelSelect() {
        const { softwareVersion } = this.state
        return (
            <div className="max-w-xs mb-4">
                <label className="dark:text-white text-sm">{getString('software_manager.model')}</label>
                <select
                    className="pretty-input"
                    value={softwareVersion!.model as number}
                    onChange={(e: any) => this.handleModelChange(Number(e.target.value))}
                    disabled={this.props.softwareVersionId !== undefined}
                >
                    <option value={ClientModel.Standard}>{getString('client_model.standard')}</option>
                    <option value={ClientModel.LTI525S}>{getString('client_model.lti525s')}</option>
                    <option value={ClientModel.SYRUP_TOWER}>{getString('client_model.syrup_tower')}</option>
                    <option value={ClientModel.FILTER_FILLER}>{getString('client_model.filter_filler')}</option>
                    <option value={ClientModel.LTSP101}>{getString('client_model.ltsp101')}</option>
                    <option value={ClientModel.LTSP70}>{getString('client_model.ltsp70')}</option>
                    <option value={ClientModel.LTSP175}>{getString('client_model.ltsp175')}</option>
                    <option value={ClientModel.LTCB220}>{getString('client_model.ltcb220')}</option>
                    <option value={ClientModel.LTCB226}>{getString('client_model.ltcb226')}</option>
                </select>
            </div>
        )
    }

    renderComponentSelect() {
        const { softwareVersion } = this.state
        const model = Number(softwareVersion!.model)
        const isC6Capable = (C6_COMPONENT_MODELS as ClientModel[]).includes(model)
        return (
            <div className="max-w-xs mb-4">
                <label className="dark:text-white text-sm">{getString('software_manager.component')}</label>
                <select
                    className="pretty-input"
                    value={softwareVersion!.component as string}
                    onChange={(e: any) => this.handleChange('component', e.target.value)}
                    disabled={!isC6Capable}
                >
                    <option value="app">{getString('software_manager.component_app')}</option>
                    {isC6Capable && <option value="c6">{getString('software_manager.component_c6')}</option>}
                </select>
            </div>
        )
    }

    renderVersionInput() {
        const { softwareVersion, errors } = this.state
        const error = errors.version !== undefined
        return (
            <div className="max-w-xs">
                <label className="dark:text-white text-sm">{getString('software_manager.version')}</label>
                <input
                    className={classNames('pretty-input ltr', error ? 'border-red-500' : 'border-gray-300')}
                    value={softwareVersion!.version as string}
                    onInput={(e: any) => this.handleChange('version', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.version!)}
            </div>
        )
    }

    renderChangeLogInput() {
        const { softwareVersion, errors } = this.state
        const error = errors.changeLog !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('software_manager.change_log')}</label>
                <textarea
                    className={classNames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={softwareVersion!.changeLog as string || ''}
                    onInput={(e: any) => this.handleChange('changeLog', e.target.value)}
                    rows={6}
                />
                {error && this.renderInputErrorMessage(errors.changeLog!)}
            </div>
        )
    }

    renderEspBinInput() {
        if (this.props.softwareVersionId && !this.state.binFile) return undefined
        return (
            <EspBinInput
                className="mt-10 shadow-none"
                onChange={this.handleEspBinChange.bind(this)}
                binFile={this.state.binFile}
            />
        )
    }
}
